import { WSSelectProps } from "@wingspanhq/fe-component-library";
import {
  EnhancedDueDiligenceAnnualRevenue as AnnualRevenueRange,
  EnhancedDueDiligenceBusinessOperatingGeography as BusinessOperatingGeography,
  EnhancedDueDiligenceCashFlow as CashFlowDescription,
  EnhancedDueDiligenceMonthlyTransactionVolume as MonthlyTransactionVolume,
  EnhancedDueDiligencePrimaryCustomerType as PrimaryCustomerType,
  EnhancedDueDiligenceProhibitedJurisdictionExposure as ProhibitedJurisdictionExposure,
  EnhancedDueDiligencePurposeOfAccount as AccountPurpose,
  EnhancedDueDiligenceSourceOfFunds as SourceOfFunds
} from "@wingspanhq/wingspan-api-sdk";

export interface EDDQuestionnaireFormData {
  purposeOfAccount: AccountPurpose;
  cashFlow: CashFlowDescription;
  monthlyTransactionVolume: MonthlyTransactionVolume;
  sourceOfFunds: SourceOfFunds;
  annualRevenue: AnnualRevenueRange;
  businessOperatingGeography: BusinessOperatingGeography;
  primaryCustomerType: PrimaryCustomerType;
  prohibitedJurisdictionExposure: ProhibitedJurisdictionExposure;
  prohibitedJurisdictionExposureDetails?: string;
}

export { ProhibitedJurisdictionExposure };

const accountPurposeOptions: WSSelectProps["options"] = [
  {
    value: AccountPurpose.RECEIVING_PAYMENTS,
    label: "Receiving payments from customers/clients"
  },
  {
    value: AccountPurpose.PAYING_SUPPLIERS_CONTRACTORS_OR_VENDORS,
    label: "Paying suppliers, contractors, or vendors"
  },
  {
    value: AccountPurpose.MANAGING_BUSINESS_SAVINGS_OR_INVESTMENTS,
    label: "Managing business savings or investments"
  },
  {
    value: AccountPurpose.SENDING_OR_RECEIVING_INTERNATIONAL_PAYMENTS,
    label: "Sending or receiving international payments"
  },
  {
    value: AccountPurpose.HANDLING_MOSTLY_CASH_RELATED_TRANSACTIONS,
    label: "Handling mostly cash-related transactions"
  }
];

const cashFlowOptions: WSSelectProps["options"] = [
  {
    value: CashFlowDescription.STRUGGLE_TO_COVER_EXPENSES,
    label: "We often struggle to cover expenses or rely on outside funding"
  },
  {
    value: CashFlowDescription.INCONSISTENT_REVENUE_VARIES_MONTHLY,
    label: "Our revenue is inconsistent and varies month-to-month"
  },
  {
    value: CashFlowDescription.FAIRLY_STEADY_SLIM_MARGINS,
    label: "Our revenue is fairly steady, but we operate with slim margins"
  },
  {
    value: CashFlowDescription.CONSISTENT_REVENUE_HEALTHY_RESERVES,
    label: "Our revenue is consistent and we maintain healthy reserves"
  },
  {
    value: CashFlowDescription.PRIMARILY_DEAL_IN_CASH,
    label: "We primarily deal in cash transactions"
  },
  {
    value: CashFlowDescription.HARD_TO_SAY_DONT_TRACK_CLOSELY,
    label: "It's hard to say / We don't track closely"
  }
];

const monthlyTransactionVolumeOptions: WSSelectProps["options"] = [
  {
    value: MonthlyTransactionVolume.BETWEEN10_K_AND50_K,
    label: "$10,000 - $50,000"
  },
  {
    value: MonthlyTransactionVolume.BETWEEN50_K_AND100_K,
    label: "$50,000 - $100,000"
  },
  {
    value: MonthlyTransactionVolume.BETWEEN100_K_AND500_K,
    label: "$100,000 - $500,000"
  },
  { value: MonthlyTransactionVolume.OVER500_K, label: "Over $500,000" }
];
const sourceOfFundsOptions: WSSelectProps["options"] = [
  {
    value: SourceOfFunds.SALES_REVENUE_FROM_PRODUCTS_OR_SERVICES,
    label: "Sales revenue from products or services"
  },
  {
    value: SourceOfFunds.CAPITAL_FROM_INVESTORS_OR_SHAREHOLDERS,
    label: "Capital from investors or shareholders"
  },
  {
    value: SourceOfFunds.LOANS_OR_LINES_OF_CREDIT,
    label: "Loans or lines of credit"
  },
  {
    value: SourceOfFunds.GOVERNMENT_OR_PRIVATE_GRANTS,
    label: "Government or private grants"
  },
  {
    value: SourceOfFunds.PRIMARILY_CASH_RECEIPTS,
    label: "Primarily cash receipts"
  }
];

const annualRevenueRangeOptions: WSSelectProps["options"] = [
  { value: AnnualRevenueRange.UP_TO250_K, label: "Up to $250,000" },
  {
    value: AnnualRevenueRange.BETWEEN250_K_AND500_K,
    label: "$250,000 - $500,000"
  },
  {
    value: AnnualRevenueRange.BETWEEN500_K_AND1_M,
    label: "$500,000 - $1,000,000"
  },
  {
    value: AnnualRevenueRange.BETWEEN1_M_AND5_M,
    label: "$1,000,000 - $5,000,000"
  },
  { value: AnnualRevenueRange.OVER5_M, label: "Over $5,000,000" }
];

const businessOperatingGeographyOptions: WSSelectProps["options"] = [
  {
    value: BusinessOperatingGeography.WITHIN_LOCAL_CITY_OR_STATE,
    label: "Primarily within local city or state"
  },
  {
    value: BusinessOperatingGeography.ACROSS_MULTIPLE_US_STATES,
    label: "Across multiple U.S. states"
  },
  {
    value: BusinessOperatingGeography.ACROSS_THE_UNITED_STATES,
    label: "Across the United States"
  },
  {
    value: BusinessOperatingGeography.INTERNATIONAL_CANADA_WESTERN_EUROPE,
    label: "International (Canada, Western Europe, Australia)"
  },
  {
    value:
      BusinessOperatingGeography.INTERNATIONAL_LATIN_AMERICA_ASIA_AFRICA_MIDDLE_EAST,
    label: "International (Latin America, Asia, Africa, Middle East)"
  },
  {
    value: BusinessOperatingGeography.PRIMARY_INTERNATIONAL_LIMITED_US_PRESENCE,
    label: "Primarily international with limited U.S. presence"
  }
];

const primaryCustomerTypeOptions: WSSelectProps["options"] = [
  {
    value: PrimaryCustomerType.INDIVIDUAL_CONSUMERS_B2_C,
    label: "Individual consumers (B2C)"
  },
  {
    value: PrimaryCustomerType.SMALL_OR_MEDIUM_BUSINESSES_B2_B,
    label: "Small or medium-sized businesses (B2B)"
  },
  {
    value: PrimaryCustomerType.LARGE_ENTERPRISES_OR_GOVERNMENT_AGENCIES,
    label: "Large enterprises or government agencies"
  },
  {
    value: PrimaryCustomerType.PRIMARILY_CASH_PAYING_CUSTOMERS,
    label: "Primarily cash-paying customers"
  },
  {
    value: PrimaryCustomerType.PRIMARILY_ONLINE_DIGITAL_CUSTOMERS,
    label: "Primarily online/digital customers"
  },
  {
    value: PrimaryCustomerType.INTERNATIONAL_CUSTOMERS_OUTSIDE_US,
    label: "International customers (outside the U.S.)"
  },
  {
    value: PrimaryCustomerType.CUSTOMERS_IN_HIGH_CASH_FLOW_INDUSTRIES,
    label: "Customers in high-cash-flow industries"
  }
];

const prohibitedJurisdictionExposureOptions: WSSelectProps["options"] = [
  {
    value: ProhibitedJurisdictionExposure.FALSE,
    label:
      "No - we do not have clients, contractors, or vendors in any of these jurisdictions"
  },
  {
    value: ProhibitedJurisdictionExposure.TRUE,
    label: "Yes - please describe"
  },
  {
    value: ProhibitedJurisdictionExposure.UNSURE,
    label: "Unsure - I would need to verify"
  }
];

export const EDD_FIELDS: {
  title: string;
  name: string;
  label: string;
  componentProps: Omit<WSSelectProps, "name" | "onChange" | "value">;
}[] = [
  {
    title: "Account purpose",
    name: "purposeOfAccount",
    label:
      "In addition to payroll processing, what is the primary other purpose you have for using our services?",
    componentProps: {
      options: accountPurposeOptions
    }
  },
  {
    title: "Cash flow",
    name: "cashFlow",
    label: "How would you best describe your business's cash flow?",
    componentProps: {
      options: cashFlowOptions
    }
  },
  {
    title: "Monthly transaction volume",
    name: "monthlyTransactionVolume",
    label:
      "What is your business's expected monthly transaction volume, including payroll and other transactions?",
    componentProps: {
      options: monthlyTransactionVolumeOptions
    }
  },
  {
    title: "Source of funds",
    name: "sourceOfFunds",
    label:
      "What are the primary sources of funds for your business? (Select all that apply)",
    componentProps: {
      options: sourceOfFundsOptions
    }
  },
  {
    title: "Annual revenue range",
    name: "annualRevenue",
    label: "What is your company's annual revenue range?",
    componentProps: {
      options: annualRevenueRangeOptions
    }
  },
  {
    title: "Business operating geography",
    name: "businessOperatingGeography",
    label: "Where does your business primarily operate and transact?",
    componentProps: {
      options: businessOperatingGeographyOptions
    }
  },
  {
    title: "Primary customers",
    name: "primaryCustomerType",
    label: "Who are your primary customers?",
    componentProps: {
      options: primaryCustomerTypeOptions
    }
  },
  {
    title: "Prohibited jurisdiction exposure",
    name: "prohibitedJurisdictionExposure",
    label:
      "Does your business have any clients, contractors, or vendors located in, or do you process payments to or from, any of the following jurisdictions: Cuba, Iran, North Korea, Syria, Russia, Belarus, or any other country on the OFAC Specially Designated Nationals list or Wingspan's Prohibited Countries List?",
    componentProps: {
      options: prohibitedJurisdictionExposureOptions
    }
  }
];
